/**
 * Pure permission decision for the WBR executive summary (Narrative) edit
 * affordance. Extracted from `WBRCalloutsViewer.canEditSummary` so the decision
 * is a single source of truth that is directly unit/property testable (mirrors
 * the `summaryDerive.ts` extraction for this same feature).
 *
 * Decision (matches the callouts edit role model — see `canEditCallout`):
 *   same-team admin/operator, or FINN-team (team_id=1) admin/operator
 *   cross-team; user role denied.
 */
export interface CanEditSummaryInput {
  /** True when the viewer is in the legacy readonly fallback mode. */
  readonlyFallback: boolean;
  /** Resolved effective role: "admin" | "operator" | "user". */
  effectiveRole: string;
  /** The viewer's active team id (FINN team is 1). */
  currentTeamId: number;
  /** The team that owns the Narrative, or null when unknown. */
  narrativeTeamId: number | null;
}

/**
 * Whether the viewer may edit the Narrative owned by `narrativeTeamId`.
 */
export function canEditSummaryDecision(input: CanEditSummaryInput): boolean {
  const { readonlyFallback, effectiveRole, currentTeamId, narrativeTeamId } =
    input;
  if (readonlyFallback) return false;
  if (narrativeTeamId == null) return false;
  const isFinnCrossTeam =
    currentTeamId === 1 &&
    (effectiveRole === "admin" || effectiveRole === "operator");
  if (isFinnCrossTeam) return true;
  if (effectiveRole === "user") return false;
  // Same-team: admin/operator only (user role denied) may edit their team's Narrative.
  return currentTeamId === narrativeTeamId;
}
